import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Mic, MicOff, AlertCircle } from 'lucide-react';
import usePTT from '../hooks/usePTT';
import useStore from '../store/useStore';

// Roger beep — bip local de confirmación al soltar el PTT (no se transmite)
function playRogerBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const g = ctx.createGain();
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.16, ctx.currentTime);
    [[1320, 0, 0.07], [990, 0.085, 0.09]].forEach(([f, s, d]) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(f, ctx.currentTime + s);
      o.connect(g);
      o.start(ctx.currentTime + s);
      o.stop(ctx.currentTime + s + d);
    });
    setTimeout(() => { try { ctx.close(); } catch (_) {} }, 400);
  } catch (_) {}
}

export default function PTTButton() {
  const [pressed, setPressed] = useState(false);
  const { startTransmitting, stopTransmitting, requestPermission, error, permissionGranted } = usePTT();

  const currentChannel  = useStore(s => s.currentChannel);
  const isTransmitting  = useStore(s => s.isTransmitting);
  const transmittingUser = useStore(s => s.transmittingUser);
  const user            = useStore(s => s.user);

  const pressedRef = useRef(false);
  const [feedback, setFeedback] = useState(null);

  // Haptics (flota Android) + roger beep al soltar — solo feedback local
  const fxReadyRef = useRef(false);
  useEffect(() => {
    if (!fxReadyRef.current) { fxReadyRef.current = true; return; }
    try { navigator.vibrate?.(pressed ? 28 : 14); } catch (_) {}
    if (!pressed) playRogerBeep();
  }, [pressed]);

  const otherTransmitting = isTransmitting && transmittingUser?.userId !== user?.id;
  const isBlocked = otherTransmitting;

  const handlePress = useCallback(async (e) => {
    e.preventDefault();
    if (pressedRef.current || !currentChannel || isBlocked) return;
    pressedRef.current = true;
    setPressed(true);
    setFeedback('on');
    setTimeout(() => setFeedback(null), 200);
    await startTransmitting();
  }, [currentChannel, isBlocked, startTransmitting]);

  const handleRelease = useCallback((e) => {
    e.preventDefault();
    if (!pressedRef.current) return;
    pressedRef.current = false;
    setPressed(false);
    setFeedback('off');
    setTimeout(() => setFeedback(null), 200);
    stopTransmitting();
  }, [stopTransmitting]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat && !pressedRef.current) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        handlePress(e);
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space') { e.preventDefault(); handleRelease(e); }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
  }, [handlePress, handleRelease]);

  if (!currentChannel) return null;

  // Talk2 palette: blue when idle, red-pink when transmitting
  const idleHueA = '#007AFF';
  const idleHueB = '#5856D6';
  const txHueA = '#FF2D55';
  const txHueB = '#FF3B30';

  const labelText = pressed
    ? '● TRANSMITIENDO'
    : isBlocked
      ? `📡 ${transmittingUser?.username} transmitiendo…`
      : 'MANTÉN PARA HABLAR';

  const labelColor = pressed ? '#FF2D55' : isBlocked ? '#86868B' : '#86868B';

  const bgGradient = pressed
    ? `radial-gradient(circle at 35% 35%, ${txHueA}, ${txHueB})`
    : isBlocked
      ? 'radial-gradient(circle at 35% 35%, #D2D2D7, #AEAEB2)'
      : `radial-gradient(circle at 35% 35%, ${idleHueA}, ${idleHueB})`;

  const ringColor = pressed ? txHueA : isBlocked ? '#AEAEB2' : idleHueA;

  const glow = pressed
    ? `0 0 50px rgba(255, 45, 85, 0.55), 0 12px 30px rgba(255,45,85,.45)`
    : isBlocked
      ? 'none'
      : `0 0 30px rgba(0, 122, 255, 0.30), 0 12px 30px rgba(0,122,255,.30)`;

  // Responsive sizing via Tailwind. Mobile is the field device (TC15, gloves):
  // the button must be impossible to miss.
  const btnSize = 'w-28 h-28 md:w-32 md:h-32';

  return (
    <div className="flex flex-col items-center gap-2 md:gap-4">
      {!permissionGranted && !error && (
        <button onClick={requestPermission}
          className="flex items-center gap-2 text-xs px-4 py-2 rounded-full font-semibold transition-all"
          style={{ background: '#E6F1FF', color: '#007AFF', border: 'none', cursor: 'pointer' }}>
          <Mic size={13} /> Permitir micrófono
        </button>
      )}
      {error && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-xs px-4 py-2 rounded-md font-semibold"
            style={{ color: '#FF3B30', background: 'rgba(255,59,48,0.08)', border: '1px solid rgba(255,59,48,0.20)' }}>
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
          {error.includes('denegado') && (
            <button onClick={requestPermission}
              className="text-xs px-3 py-1.5 rounded-full font-semibold"
              style={{ background: '#E6F1FF', color: '#007AFF', border: 'none', cursor: 'pointer' }}>
              Reintentar
            </button>
          )}
        </div>
      )}

      <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: labelColor }}>
        {labelText}
      </div>

      <button
        onMouseDown={handlePress} onMouseUp={handleRelease}
        onTouchStart={handlePress} onTouchEnd={handleRelease} onTouchCancel={handleRelease}
        onContextMenu={(e) => e.preventDefault()}
        disabled={isBlocked}
        className={`relative ${btnSize} rounded-full flex items-center justify-center select-none touch-none cursor-pointer transition-all duration-150 ${pressed ? 'scale-95' : 'hover:scale-105 active:scale-95'} ${isBlocked ? 'cursor-not-allowed opacity-60' : ''}`}
        style={{
          background: bgGradient,
          border: `4px solid ${ringColor}`,
          boxShadow: glow,
          color: '#fff',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: 'none',
        }}
        aria-label="Push to Talk"
      >
        {pressed && (
          <>
            <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: `${txHueA}40`, animationDuration: '1s' }} />
            <div className="absolute -inset-3 rounded-full animate-ping opacity-20" style={{ background: `${txHueA}30`, animationDuration: '1.5s', animationDelay: '0.2s' }} />
            <div className="fx-sweep" aria-hidden="true" />
          </>
        )}
        <div className="relative z-10">
          {pressed
            ? <Mic className="w-7 h-7 md:w-10 md:h-10" style={{ color: '#fff' }} />
            : isBlocked
              ? <MicOff className="w-6 h-6 md:w-9 md:h-9" style={{ color: '#fff' }} />
              : <Mic className="w-6 h-6 md:w-9 md:h-9" style={{ color: '#fff' }} />}
        </div>
      </button>

      <p className="hidden md:block text-xs text-n-600">
        {pressed ? 'Suelta para terminar' : 'o presiona'}&nbsp;
        <kbd className="px-1.5 py-0.5 rounded font-mono text-[11px]"
          style={{ background: '#F5F5F7', color: '#48484A', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'inset 0 -1px 0 rgba(0,0,0,.05)' }}>
          SPACE
        </kbd>
      </p>
    </div>
  );
}
